/**
 * Wall junctions — filling the wedge two walls leave between them.
 *
 * Walls are independent stroked segments, and their caps are **butt** on
 * purpose: a cap that overshoots (`square`, `round`) draws the wall longer than
 * the length the inspector reports for it, which in an authoring tool people
 * measure in is a lie. The honest cap leaves an empty angle wherever two walls
 * meet, so the angle is filled explicitly instead — one polygon per junction,
 * in the wall colour.
 *
 * The polygon is the convex hull of every corner the meeting walls contribute,
 * plus the intersections of their edge lines. At a right angle that hull is
 * exactly the mitred corner; at three or more arms it is the smallest convex
 * cap over all of them. Intersections further than `miterLimit` half-widths
 * away are dropped, which is what stops two nearly-parallel walls from growing
 * the long spike a true miter would produce.
 */
import type { Point } from './geometry'

export interface JunctionWall {
    start: Point
    end: Point
    thickness: number
}

/** Endpoints are snapped, so 0.01 mm is far below "the same point". */
const KEY_SCALE = 100

/** How far a mitred corner may reach, in half-widths, before it is dropped. */
const DEFAULT_MITER_LIMIT = 2.5

interface Arm {
    /** Unit direction pointing **away** from the junction, down the wall. */
    dir: Point
    /** Half the wall's thickness. */
    half: number
}

const keyOf = (p: Point): string =>
    `${Math.round(p.x * KEY_SCALE)}:${Math.round(p.y * KEY_SCALE)}`

/**
 * Where two infinite lines cross, or null when they are parallel.
 * Each line is a point and a direction.
 */
function lineIntersection(a: Point, da: Point, b: Point, db: Point): Point | null {
    const denom = da.x * db.y - da.y * db.x
    if (Math.abs(denom) < 1e-9) return null
    const t = ((b.x - a.x) * db.y - (b.y - a.y) * db.x) / denom
    return { x: a.x + da.x * t, y: a.y + da.y * t }
}

/** Andrew's monotone chain, counter-clockwise, without collinear points. */
export function convexHull(points: Point[]): Point[] {
    const pts = [...points].sort((p, q) => (p.x - q.x) || (p.y - q.y))
    if (pts.length < 3) return pts
    const cross = (o: Point, a: Point, b: Point): number =>
        (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)

    const half = (source: Point[]): Point[] => {
        const out: Point[] = []
        for (const p of source) {
            while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], p) <= 0) out.pop()
            out.push(p)
        }
        out.pop()
        return out
    }
    return [...half(pts), ...half([...pts].reverse())]
}

/**
 * One filled polygon per point where two or more walls meet. Walls with no
 * thickness, and endpoints nothing else touches, contribute nothing.
 */
export function wallJunctionPolygons(
    walls: JunctionWall[],
    { miterLimit = DEFAULT_MITER_LIMIT }: { miterLimit?: number } = {},
): Point[][] {
    const junctions = new Map<string, { at: Point; arms: Arm[] }>()

    const add = (at: Point, dir: Point, half: number): void => {
        const key = keyOf(at)
        const entry = junctions.get(key) ?? { at, arms: [] }
        entry.arms.push({ dir, half })
        junctions.set(key, entry)
    }

    for (const wall of walls) {
        const dx = wall.end.x - wall.start.x
        const dy = wall.end.y - wall.start.y
        const length = Math.hypot(dx, dy)
        const half = Math.max(wall.thickness, 0) / 2
        if (length < 1e-6 || half <= 0) continue
        const ux = dx / length
        const uy = dy / length
        add(wall.start, { x: ux, y: uy }, half)
        add(wall.end, { x: -ux, y: -uy }, half)
    }

    const polygons: Point[][] = []
    for (const { at, arms } of junctions.values()) {
        if (arms.length < 2) continue

        const corners: Point[] = []
        for (const arm of arms) {
            // The butt end's two corners: the axis normal, scaled to half-width.
            const nx = -arm.dir.y * arm.half
            const ny = arm.dir.x * arm.half
            corners.push({ x: at.x + nx, y: at.y + ny }, { x: at.x - nx, y: at.y - ny })
        }

        const reach = miterLimit * Math.max(...arms.map((a) => a.half))
        const candidates = [...corners]
        for (let i = 0; i < arms.length; i++) {
            for (let j = i + 1; j < arms.length; j++) {
                for (const si of [1, -1]) {
                    for (const sj of [1, -1]) {
                        const a = arms[i]
                        const b = arms[j]
                        const pa = { x: at.x - a.dir.y * a.half * si, y: at.y + a.dir.x * a.half * si }
                        const pb = { x: at.x - b.dir.y * b.half * sj, y: at.y + b.dir.x * b.half * sj }
                        const hit = lineIntersection(pa, a.dir, pb, b.dir)
                        if (!hit) continue
                        if (Math.hypot(hit.x - at.x, hit.y - at.y) > reach) continue
                        candidates.push(hit)
                    }
                }
            }
        }

        const hull = convexHull(candidates)
        if (hull.length >= 3) polygons.push(hull)
    }
    return polygons
}
